import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

function Signup() {
  const [coachName, setCoachName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [sport, setSport] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Club search
  const [clubSearch, setClubSearch] = useState('');
  const [clubLocation, setClubLocation] = useState('');
  const [suggestedClubs, setSuggestedClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [showNewClub, setShowNewClub] = useState(false);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const sports = [
    'Football (Soccer)',
    'Basketball',
    'Netball',
    'Football (AFL)',
    'Rugby League',
    'Rugby Union',
    'Cricket',
    'Hockey',
    'Futsal',
    'Other'
  ];

  // Fuzzy search clubs - works for unauthenticated users
  useEffect(() => {
    const searchClubs = async () => {
      if (clubSearch.length < 2) {
        setSuggestedClubs([]);
        return;
      }

      try {
        const searchLower = clubSearch.toLowerCase().trim();
        
        // Search clubs collection (public read access)
        const clubsRef = collection(db, 'clubs');
        const clubsSnapshot = await getDocs(clubsRef);
        
        const clubs = [];
        clubsSnapshot.forEach((doc) => {
          const club = { id: doc.id, ...doc.data() };
          const clubNameLower = club.name.toLowerCase();
          
          // Flexible fuzzy matching
          if (clubNameLower.includes(searchLower) || 
              searchLower.includes(clubNameLower) ||
              clubNameLower.split(' ').some(word => word.startsWith(searchLower))) {
            clubs.push({
              id: club.id,
              name: club.name,
              location: club.location,
              teamCount: club.teamCount || 1
            });
          }
        });
        
        // Sort by relevance
        clubs.sort((a, b) => {
          // Exact matches first
          const aExact = a.name.toLowerCase() === searchLower;
          const bExact = b.name.toLowerCase() === searchLower;
          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;
          
          // Starts with second
          const aStarts = a.name.toLowerCase().startsWith(searchLower);
          const bStarts = b.name.toLowerCase().startsWith(searchLower);
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          
          // Most popular third
          return (b.teamCount || 0) - (a.teamCount || 0);
        });
        
        setSuggestedClubs(clubs.slice(0, 10)); // Top 10 results
      } catch (err) {
        console.error('Error searching clubs:', err);
        // Fail silently - user can still create new club
      }
    };

    const debounce = setTimeout(searchClubs, 300);
    return () => clearTimeout(debounce);
  }, [clubSearch]);

  const selectClub = (club) => {
    setSelectedClub(club);
    setClubSearch(club.name);
    setClubLocation(club.location);
    setSuggestedClubs([]);
    setShowNewClub(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!coachName || !teamName || !sport || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!clubSearch || (!selectedClub && !showNewClub)) {
      setError('Please select or create a club');
      return;
    }

    if (showNewClub && !clubLocation) {
      setError('Please enter club location');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      let clubId;
      let finalClubName = clubSearch.trim();
      let finalClubLocation = clubLocation.trim();
      
      // Handle club creation/selection
      if (showNewClub || !selectedClub) {
        // Creating new club
        const clubRef = doc(collection(db, 'clubs'));
        await setDoc(clubRef, {
          name: finalClubName,
          location: finalClubLocation,
          createdAt: new Date().toISOString(),
          teamCount: 1
        });
        clubId = clubRef.id;
      } else {
        // Using existing club
        clubId = selectedClub.id;
        finalClubName = selectedClub.name;
        finalClubLocation = selectedClub.location;
        
        // Update team count
        await setDoc(doc(db, 'clubs', clubId), {
          teamCount: (selectedClub.teamCount || 0) + 1,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      }

      // Create coach/team document
      await setDoc(doc(db, 'teams', user.uid), {
        coachName: coachName.trim(),
        teamName: teamName.trim(),
        sport: sport,
        email,
        clubId,
        clubName: finalClubName,
        clubLocation: finalClubLocation,
        createdAt: new Date().toISOString(),
        players: [],
        votes: []
      });

      // Success! User will be auto-redirected by auth state change
      console.log('Signup successful!');

    } catch (err) {
      console.error('Signup error:', err);
      
      if (err.code === 'auth/email-already-in-use') {
        setError('Email already in use');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'permission-denied') {
        setError('Database permission error. Please contact support.');
      } else {
        setError(`Error creating account: ${err.message}`);
      }
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="header">
        <h1>Create Account</h1>
        <button 
          onClick={() => navigate('/login')}
          style={{
            background: 'white',
            border: '2px solid white',
            color: '#243665',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: '600',
            minWidth: '80px',
            minHeight: '44px',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          Back
        </button>
      </div>

      <div style={{ padding: '20px 0' }}>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
          Join thousands of coaches using Pitchallo-motm
        </p>

        <form onSubmit={handleSignup}>
          <div className="card">
            <h3 style={{ marginBottom: '16px', color: '#243665' }}>Your Details</h3>
            
            <div>
              <label className="input-label">COACH NAME *</label>
              <input
                type="text"
                className="input"
                value={coachName}
                onChange={(e) => setCoachName(e.target.value)}
                placeholder="John Smith"
              />
            </div>

            <div>
              <label className="input-label">TEAM NAME *</label>
              <input
                type="text"
                className="input"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Warriors U12"
              />
            </div>

            <div>
              <label className="input-label">SPORT *</label>
              <select
                className="input"
                value={sport}
                onChange={(e) => setSport(e.target.value)}
                style={{
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23243665' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '20px',
                  paddingRight: '40px'
                }}
              >
                <option value="">Select your sport...</option>
                {sports.map((sportOption) => (
                  <option key={sportOption} value={sportOption}>
                    {sportOption}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card">
            <h3 style={{ marginBottom: '16px', color: '#243665' }}>Your Club</h3>
            
            <div>
              <label className="input-label">CLUB NAME *</label>
              <input
                type="text"
                className="input"
                value={clubSearch}
                onChange={(e) => {
                  setClubSearch(e.target.value);
                  setSelectedClub(null);
                  setShowNewClub(false);
                }}
                placeholder="Search for your club..."
              />
              <p style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                Start typing to search existing clubs
              </p>
            </div>

            {suggestedClubs.length > 0 && (
              <div style={{
                background: 'white',
                border: '2px solid #243665',
                borderRadius: '8px',
                marginBottom: '12px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {suggestedClubs.map((club, index) => (
                  <div
                    key={club.id}
                    onClick={() => selectClub(club)}
                    style={{
                      padding: '12px',
                      borderBottom: index < suggestedClubs.length - 1 ? '1px solid #eee' : 'none',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ fontWeight: '600', color: '#243665' }}>
                      {club.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {club.location} • {club.teamCount || 1} team{club.teamCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {clubSearch.length >= 2 && suggestedClubs.length === 0 && !selectedClub && (
              <div style={{
                padding: '12px',
                background: '#fff3cd',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <p style={{ fontSize: '14px', marginBottom: '8px' }}>
                  Can't find "{clubSearch}"?
                </p>
                <button
                  type="button"
                  className="button"
                  onClick={() => setShowNewClub(true)}
                >
                  Create New Club
                </button>
              </div>
            )}

            {(selectedClub || showNewClub) && (
              <div>
                <label className="input-label">LOCATION (City, State) *</label>
                <input
                  type="text"
                  className="input"
                  value={clubLocation}
                  onChange={(e) => setClubLocation(e.target.value)}
                  placeholder="Melbourne, VIC"
                  disabled={selectedClub && !showNewClub}
                />
              </div>
            )}
          </div>

          <div className="card">
            <h3 style={{ marginBottom: '16px', color: '#243665' }}>Account Security</h3>
            
            <div>
              <label className="input-label">EMAIL *</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="coach@example.com"
                autoCapitalize="none"
              />
            </div>

            <div>
              <label className="input-label">PASSWORD *</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 characters"
              />
            </div>

            <div>
              <label className="input-label">CONFIRM PASSWORD *</label>
              <input
                type="password"
                className="input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
              />
            </div>
          </div>

          {error && <p className="error">{error}</p>}

          <button 
            type="submit" 
            className="button"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <button
            type="button"
            className="text-link"
            onClick={() => navigate('/login')}
          >
            Already have an account? Login
          </button>
        </form>
      </div>

      <div className="pitchallo-footer">
        <p>Powered by <a href="https://pitchallo.com" target="_blank" rel="noopener noreferrer">Pitchallo</a></p>
      </div>
    </div>
  );
}

export default Signup;
